import type { Doc } from "../../_generated/dataModel";
import type { FilterRule, FilterRuleCtx } from "../../lib/types/filters_types";
import { getTableConfig } from "../../lib/shared/table_search/helpers";
import { resolveEffectiveScope } from "../../lib/shared/table_search/scope";
import { getAccessibleCollection } from "./helpers";

/**
 * Registros de UNA colección para el paginado estándar. Sin `collectionId`
 * no devuelve nada (nunca se listan registros de todas las colecciones), y sin
 * acceso a la colección (roles) tampoco. `appliesWithoutArgs` fuerza el camino
 * en memoria, que entiende esta regla (ver index_paginate.ts).
 *
 * Respeta `masterDataScope` de la empresa: con "branch" filtra por
 * `internalControl.branchId` (salvo super_admin). Con "company" (default) usa
 * el índice `by_company_id` como antes.
 */
export const collectionRecordsRule: FilterRule<Doc<"collection_records">> = {
  priority: 0,
  appliesWithoutArgs: true,
  build: async (ctx: FilterRuleCtx, args) => {
    const { collectionId, includeDeleted } = args as {
      collectionId?: string;
      includeDeleted?: boolean;
    };
    if (!collectionId) return [];

    const collection = await getAccessibleCollection(
      ctx.rawCtx,
      ctx.userContext,
      collectionId,
    );
    if (!collection) return [];

    const scope = resolveEffectiveScope(
      getTableConfig("collection_records"),
      ctx.userContext,
    );
    const byBranch = scope === "branch" && Boolean(ctx.userContext.branchId);

    const rows = byBranch
      ? await ctx.rawCtx.db
          .query("collection_records")
          .withIndex("by_company_and_branch", (q: any) =>
            q
              .eq("internalControl.companyId", collection.internalControl.companyId)
              .eq("internalControl.branchId", ctx.userContext.branchId),
          )
          .order("desc")
          .collect()
      : await ctx.rawCtx.db
          .query("collection_records")
          .withIndex("by_company_id", (q: any) =>
            q.eq("internalControl.companyId", collection.internalControl.companyId),
          )
          .order("desc")
          .collect();

    return rows
      .filter((row) => row.collectionId === collection._id)
      .filter((row) =>
        includeDeleted ? true : row.internalControl.state !== "deleted",
      );
  },
};
